import { PersonaScope } from '@app/modules/payroll/components/kyc-kyb/types';

export type PersonaInquiryDetails = {
    status: PersonaInquiryStatus;
    inquiryId: string;
    inquiryType: Lowercase<PersonaScope> | 'ubo';
};

/**
 * Possible inquiry statuses returned from Persona
 */
export type PersonaInquiryStatus =
    | 'created'
    | 'pending'
    | 'completed'
    | 'expired'
    | 'failed'
    | 'needs_review'
    | 'approved'
    | 'declined';

/**
 * Possible inquiry statuses returned from Humi
 */
export type HumiInquiryStatus = 'not_complete' | 'in_progress' | 'in_review' | 'complete' | 'approved' | 'declined';

/**
 * When starting a Persona Session from scratch, the following attributes are required
 */
export type NewPersonaSessionAttributes = {
    templateId: string;
    referenceId: string;
    environmentId: string;
};

/**
 * When resuming an existing Persona Session, the following attributes are required
 */
export type ResumePersonaSessionAttributes = {
    inquiryId: string;
    sessionToken: string;
};

/**
 * These statuses rely on intervention from a 3rd-party. Therefore we want to ignore the cache if an inquiry is in these statuses so we always get a live reflection of whether it's been actioned on
 */
export const INVALIDATE_CACHE_STATUSES: PersonaInquiryStatus[] = ['failed', 'needs_review'];

/**
 * These statuses indicate the inquiry has reached an incomplete but recoverable terminal state . Therefore the Inquiry can be restarted by the user to complete verification.
 */
export const INCOMPLETE_HUMI_STATUSES: HumiInquiryStatus[] = ['not_complete', 'declined'];

/**
 * Each KYC/KYB type has its own pre-redirect page under different modules.
 * This mapping both informs the link within the bar as well as informs when to hide the bar
 */
export const KYC_KYB_URLS: Record<PersonaScope, string> = {
    KYC: '/account/verify-your-identity',
    KYB: '/company/verify-your-business',
} as const;

/**
 * Minimum deadline date for KYC/KYB re-verification, checking date-only values to avoid timezone rollover issues
 */
export const KYC_KYB_RE_VERIFICATION_MINIMUM_DEADLINE = new Date(2026, 3, 19, 12, 0, 0);

/**
 * Number of days after expiration to calculate re-verification deadline
 */
export const KYC_KYB_RE_VERIFICATION_DEADLINE_DAYS = 180;

/**
 * Fields that we allow to be pre-filled into Persona for KYC prior to launching
 */
type KYCPrefillField = 'selected_country_code' | 'email_address' | 'name_first' | 'name_last';

/**
 * All KYB prefill fields. This is the single source of truth for all prefillable fields.
 */
export const KYB_PREFILL_FIELDS = [
    'business_name',
    'business_physical_address_city',
    'business_physical_address_country_code',
    'business_physical_address_full',
    'business_physical_address_postal_code',
    'business_physical_address_street_1',
    'business_physical_address_street_2',
    'business_physical_address_subdivision',
    'business_registered_address_city',
    'business_registered_address_country_code',
    'business_registered_address_postal_code',
    'business_registered_address_street_1',
    'business_registered_address_street_2',
    'business_registered_address_subdivision',
    'business_tax_identification_number',
    'director_1_address_city',
    'director_1_address_country_code',
    'director_1_address_postal_code',
    'director_1_address_street_1',
    'director_1_address_street_2',
    'director_1_address_subdivision',
    'director_1_country',
    'director_1_name_first',
    'director_1_name_last',
    'director_1_occupation',
    'director_2_address_city',
    'director_2_address_country_code',
    'director_2_address_postal_code',
    'director_2_address_street_1',
    'director_2_address_street_2',
    'director_2_address_subdivision',
    'director_2_country',
    'director_2_name_first',
    'director_2_name_last',
    'director_2_occupation',
    'director_3_address_city',
    'director_3_address_country_code',
    'director_3_address_postal_code',
    'director_3_address_street_1',
    'director_3_address_street_2',
    'director_3_address_subdivision',
    'director_3_country',
    'director_3_name_first',
    'director_3_name_last',
    'director_3_occupation',
    'director_4_address_city',
    'director_4_address_country_code',
    'director_4_address_postal_code',
    'director_4_address_street_1',
    'director_4_address_street_2',
    'director_4_address_subdivision',
    'director_4_country',
    'director_4_name_first',
    'director_4_name_last',
    'director_4_occupation',
    'director_5_address_city',
    'director_5_address_country_code',
    'director_5_address_postal_code',
    'director_5_address_street_1',
    'director_5_address_street_2',
    'director_5_address_subdivision',
    'director_5_country',
    'director_5_name_first',
    'director_5_name_last',
    'director_5_occupation',
    'director_6_address_city',
    'director_6_address_country_code',
    'director_6_address_postal_code',
    'director_6_address_street_1',
    'director_6_address_street_2',
    'director_6_address_subdivision',
    'director_6_country',
    'director_6_name_first',
    'director_6_name_last',
    'director_6_occupation',
    'director_7_address_city',
    'director_7_address_country_code',
    'director_7_address_postal_code',
    'director_7_address_street_1',
    'director_7_address_street_2',
    'director_7_address_subdivision',
    'director_7_country',
    'director_7_name_first',
    'director_7_name_last',
    'director_7_occupation',
    'director_8_address_city',
    'director_8_address_country_code',
    'director_8_address_postal_code',
    'director_8_address_street_1',
    'director_8_address_street_2',
    'director_8_address_subdivision',
    'director_8_country',
    'director_8_name_first',
    'director_8_name_last',
    'director_8_occupation',
    'director_9_address_city',
    'director_9_address_country_code',
    'director_9_address_postal_code',
    'director_9_address_street_1',
    'director_9_address_street_2',
    'director_9_address_subdivision',
    'director_9_country',
    'director_9_name_first',
    'director_9_name_last',
    'director_9_occupation',
    'director_10_address_city',
    'director_10_address_country_code',
    'director_10_address_postal_code',
    'director_10_address_street_1',
    'director_10_address_street_2',
    'director_10_address_subdivision',
    'director_10_attestation',
    'director_10_country',
    'director_10_name_first',
    'director_10_name_last',
    'director_10_occupation',
    'director_11_address_city',
    'director_11_address_country_code',
    'director_11_address_postal_code',
    'director_11_address_street_1',
    'director_11_address_street_2',
    'director_11_address_subdivision',
    'director_11_country',
    'director_11_name_first',
    'director_11_name_last',
    'director_11_occupation',
    'director_12_address_city',
    'director_12_address_country_code',
    'director_12_address_postal_code',
    'director_12_address_street_1',
    'director_12_address_street_2',
    'director_12_address_subdivision',
    'director_12_country',
    'director_12_name_first',
    'director_12_name_last',
    'director_12_occupation',
    'director_13_address_city',
    'director_13_address_country_code',
    'director_13_address_postal_code',
    'director_13_address_street_1',
    'director_13_address_street_2',
    'director_13_address_subdivision',
    'director_13_country',
    'director_13_name_first',
    'director_13_name_last',
    'director_13_occupation',
    'director_14_address_city',
    'director_14_address_country_code',
    'director_14_address_postal_code',
    'director_14_address_street_1',
    'director_14_address_street_2',
    'director_14_address_subdivision',
    'director_14_country',
    'director_14_name_first',
    'director_14_name_last',
    'director_14_occupation',
    'director_15_address_city',
    'director_15_address_country_code',
    'director_15_address_postal_code',
    'director_15_address_street_1',
    'director_15_address_street_2',
    'director_15_address_subdivision',
    'director_15_country',
    'director_15_name_first',
    'director_15_name_last',
    'director_15_occupation',
    'entity_structure',
    'form_filler_email_address',
    'form_filler_is_also_owner',
    'form_filler_is_signing_officer',
    'form_filler_job_title',
    'form_filler_name_first',
    'form_filler_name_last',
    'form_filler_percentage_ownership',
    'incorporation_year',
    'initiator_employee_id',
    'last_approved_inquiry',
    'organization_id',
    'parent_inquiry_id',
    'registered_address_same_as_physical',
    'signing_officer_api_endpoint',
    'ubo_1_email_address',
    'ubo_1_name_first',
    'ubo_1_name_last',
    'ubo_1_percentage_ownership',
    'ubo_2_email_address',
    'ubo_2_name_first',
    'ubo_2_name_last',
    'ubo_2_percentage_ownership',
    'ubo_3_email_address',
    'ubo_3_name_first',
    'ubo_3_name_last',
    'ubo_3_percentage_ownership',
    'ubo_4_email_address',
    'ubo_4_name_first',
    'ubo_4_name_last',
    'ubo_4_percentage_ownership',
    'ubo_5_email_address',
    'ubo_5_name_first',
    'ubo_5_name_last',
    'ubo_5_percentage_ownership',
    'ubo_6_email_address',
    'ubo_6_name_first',
    'ubo_6_name_last',
    'ubo_6_percentage_ownership',
    'ubo_7_email_address',
    'ubo_7_name_first',
    'ubo_7_name_last',
    'ubo_7_percentage_ownership',
    'ubo_8_email_address',
    'ubo_8_name_first',
    'ubo_8_name_last',
    'ubo_8_percentage_ownership',
    'ubo_9_email_address',
    'ubo_9_name_first',
    'ubo_9_name_last',
    'ubo_9_percentage_ownership',
    'ubo_10_email_address',
    'ubo_10_name_first',
    'ubo_10_name_last',
    'ubo_10_percentage_ownership',
] as const;

/**
 * Type derived from the KYB prefill fields array
 */
export type KYBPrefillField = (typeof KYB_PREFILL_FIELDS)[number];

/**
 * Record of pre-filled data for KYC to be sent to Persona
 */
export type KYCPrefill = Partial<Record<KYCPrefillField, string>>;
/**
 * Record of pre-filled data for KYB to be sent to Persona
 * Using Record<string, any> to allow for dynamic fields from re-verification inquiries
 */
export type KYBPrefill = Partial<Record<KYBPrefillField, string | number>> & Record<string, any>;
